// Copyright 2025, University of Maryland, All Rights Reserved
import { apiClient } from './api-client';

interface DebugSettings {
  enabled: boolean;
  updated_by?: string;
  updated_at?: string;
}

interface CreateJobResponse {
  job_id: string;
}

export const adminApi = {
  /**
   * Gets the current global debug settings
   * @returns Promise with the debug settings data
   */
  getDebugSettings: async (): Promise<DebugSettings> => {
    const response = await apiClient.get<DebugSettings>('/admin/settings/debug');
    return response.data;
  },

  /**
   * Updates the global debug settings
   * @param enabled - Whether debug mode should be enabled
   * @returns Promise with the updated debug settings
   */
  updateDebugSettings: async (enabled: boolean): Promise<{ enabled: boolean }> => {
    const response = await apiClient.put<{ enabled: boolean }>('/admin/settings/debug', { enabled });
    return response.data;
  },

  /**
   * Sync Maryland bills for a specific session
   * @param session - The legislative session to sync (e.g., '2024RS', '2025RS', '2026RS')
   * @param maxBills - Maximum number of bills to sync (optional)
   * @param force_changes - Force content_changed=true even when content is identical (optional)
   * @returns Promise with the job ID
   */
  syncMarylandBills: async (session?: string, max_bills?: number, force_changes?: boolean): Promise<CreateJobResponse> => {
    let url = '/jobs/sync-maryland';
    const params = new URLSearchParams();
    
    if (session) {
      params.append('session', session);
    }
    if (max_bills && max_bills > 0) {
      params.append('max_bills', max_bills.toString());
    }
    if (force_changes) {
      params.append('force_changes', force_changes.toString());
    }
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const response = await apiClient.post<CreateJobResponse>(url);
    return response.data;
  }
};